import { useEffect, useState } from 'react'
import { Card, Form, Button, Spinner, Accordion, Badge } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { useChannelConfig, ChannelConfigData } from '../../channelConfig/hooks/useChannelConfig'
import { IAllUserNode } from '../../../types/users'
import { parseId } from '../../../helpers'
import { useGetUser } from '../../../store/userStore'

interface Props {
    node: IAllUserNode
}

/**
 * Tab de configuración de canales reutilizando la misma UI que ChannelConfigPage
 * pero scoped a la organización del usuario que se está editando.
 */
export const UserChannelConfigTab: React.FC<Props> = ({ node }) => {
    const orgId = node.organization?.id ? `${parseId(node.organization.id)}` : undefined

    const { config, loading, saving, saveOrgConfig } = useChannelConfig(orgId)
    const [form, setForm] = useState<Partial<ChannelConfigData>>({})
    const [isDirty, setIsDirty] = useState(false)

    // Sync form when config loads
    useEffect(() => {
        if (!loading) {
            setForm(config)
            setIsDirty(false)
        }
    }, [config, loading])

    const handleChange = (field: keyof ChannelConfigData, value: any) => {
        setForm((prev) => ({ ...prev, [field]: value }))
        setIsDirty(true)
    }

    const handleSave = async () => {
        const success = await saveOrgConfig(form)
        if (success) {
            toast.success('Channel configuration saved successfully')
            setIsDirty(false)
        } else {
            toast.error('Error saving channel configuration')
        }
    }

    const userGroups = useGetUser((s) => s.user?.groups)?.map((g: any) => g.name || g) || []
    const isSuperAdmin = userGroups.includes('SuperAdministrador')
    const isAdmin = userGroups.includes('Administrador') || isSuperAdmin

    if (!isAdmin) {
        return (
            <Card className='mb-5'>
                <Card.Body className='text-center py-10'>
                    <i className='bi bi-lock-fill fs-1 text-muted mb-3 d-block' />
                    <h5 className='text-gray-700'>Access Restricted</h5>
                    <p className='text-muted'>
                        Only Administrators can configure channel overrides for users.
                    </p>
                </Card.Body>
            </Card>
        )
    }

    if (!orgId) {
        return (
            <Card className='mb-5'>
                <Card.Body className='text-center py-10'>
                    <i className='bi bi-exclamation-triangle fs-1 text-warning mb-3 d-block' />
                    <h5 className='text-gray-700'>No Organization Assigned</h5>
                    <p className='text-muted'>
                        This user must be assigned to an organization before configuring channels.
                    </p>
                </Card.Body>
            </Card>
        )
    }

    if (loading) {
        return (
            <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 300 }}>
                <Spinner animation='border' variant='primary' />
            </div>
        )
    }

    return (
        <div className='card mb-5 mb-xl-10'>
            <div className='p-5'>
                {/* Header */}
                <div className='d-flex justify-content-between align-items-center mb-5'>
                    <div>
                        <h3 className='fw-bold text-gray-900 mb-1'>
                            <i className='bi bi-broadcast me-2' />
                            Channel Configuration
                        </h3>
                        <p className='text-muted fs-7 mb-0'>
                            Configure communication channels for{' '}
                            <strong>{node.organization?.name || 'this organization'}</strong>
                        </p>
                    </div>
                    <Button
                        variant='primary'
                        onClick={handleSave}
                        disabled={saving || !isDirty}
                        className='px-6'
                    >
                        {saving ? (
                            <>
                                <Spinner animation='border' size='sm' className='me-2' />
                                Saving...
                            </>
                        ) : (
                            <>
                                <i className='bi bi-check-lg me-1' />
                                Save Changes
                            </>
                        )}
                    </Button>
                </div>

                <Accordion >
                    {/* ─── Twilio SMS ─── */}
                    <Accordion.Item eventKey='twilio'>
                        <Accordion.Header>
                            <div className='d-flex align-items-center gap-2'>
                                <i className='bi bi-chat-dots fs-4 text-info' />
                                <span className='fw-bold fs-5'>Twilio SMS</span>
                                <Badge bg={form.twilio_enabled ? 'success' : 'secondary'} className='ms-2'>
                                    {form.twilio_enabled ? 'Enabled' : 'Disabled'}
                                </Badge>
                            </div>
                        </Accordion.Header>
                        <Accordion.Body>
                            <div className='row g-4'>
                                <div className='col-12'>
                                    <Form.Check
                                        type='switch'
                                        id='user_twilio_enabled'
                                        label='Enable Twilio SMS'
                                        checked={form.twilio_enabled || false}
                                        onChange={(e) => handleChange('twilio_enabled', e.target.checked)}
                                        className='fs-5'
                                    />
                                </div>
                                <div className='col-md-6'>
                                    <Form.Group>
                                        <Form.Label className='fw-semibold text-gray-700'>Account SID</Form.Label>
                                        <Form.Control
                                            type='text'
                                            placeholder='ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'
                                            value={form.twilio_account_sid || ''}
                                            onChange={(e) => handleChange('twilio_account_sid', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className='col-md-6'>
                                    <Form.Group>
                                        <Form.Label className='fw-semibold text-gray-700'>Auth Token</Form.Label>
                                        <Form.Control
                                            type='text'
                                            autoComplete='off'
                                            placeholder='Enter auth token'
                                            value={form.twilio_auth_token || ''}
                                            onChange={(e) => handleChange('twilio_auth_token', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className='col-md-6'>
                                    <Form.Group>
                                        <Form.Label className='fw-semibold text-gray-700'>Phone Number</Form.Label>
                                        <Form.Control
                                            type='text'
                                            placeholder='+18586096799'
                                            value={form.twilio_phone_number || ''}
                                            onChange={(e) => handleChange('twilio_phone_number', e.target.value)}
                                        />
                                        <Form.Text className='text-muted'>Include country code with +</Form.Text>
                                    </Form.Group>
                                </div>
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>

                    {/* ─── Botmaker WhatsApp ─── */}
                    <Accordion.Item eventKey='botmaker' className='mt-3'>
                        <Accordion.Header>
                            <div className='d-flex align-items-center gap-2'>
                                <i className='bi bi-whatsapp fs-4 text-success' />
                                <span className='fw-bold fs-5'>Botmaker WhatsApp</span>
                                <Badge bg={form.botmaker_enabled ? 'success' : 'secondary'} className='ms-2'>
                                    {form.botmaker_enabled ? 'Enabled' : 'Disabled'}
                                </Badge>
                            </div>
                        </Accordion.Header>
                        <Accordion.Body>
                            <div className='row g-4'>
                                <div className='col-12'>
                                    <Form.Check
                                        type='switch'
                                        id='user_botmaker_enabled'
                                        label='Enable Botmaker WhatsApp'
                                        checked={form.botmaker_enabled || false}
                                        onChange={(e) => handleChange('botmaker_enabled', e.target.checked)}
                                        className='fs-5'
                                    />
                                </div>
                                <div className='col-md-6'>
                                    <Form.Group>
                                        <Form.Label className='fw-semibold text-gray-700'>API URL</Form.Label>
                                        <Form.Control
                                            type='url'
                                            placeholder='https://api.botmaker.com/v2.0'
                                            value={form.botmaker_api_url || ''}
                                            onChange={(e) => handleChange('botmaker_api_url', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className='col-md-6'>
                                    <Form.Group>
                                        <Form.Label className='fw-semibold text-gray-700'>Bot Name</Form.Label>
                                        <Form.Control
                                            type='text'
                                            placeholder='thefutureinsurance'
                                            value={form.botmaker_bot_name || ''}
                                            onChange={(e) => handleChange('botmaker_bot_name', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className='col-12'>
                                    <Form.Group>
                                        <Form.Label className='fw-semibold text-gray-700'>API Token (JWT)</Form.Label>
                                        <Form.Control
                                            as='textarea'
                                            autoComplete='off'
                                            rows={2}
                                            placeholder='eyJhbGciOiJIUzUxMiJ9...'
                                            value={form.botmaker_api_token || ''}
                                            onChange={(e) => handleChange('botmaker_api_token', e.target.value)}
                                            style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                                        />
                                    </Form.Group>
                                </div>
                                <div className='col-md-6'>
                                    <Form.Group>
                                        <Form.Label className='fw-semibold text-gray-700'>Default Channel ID</Form.Label>
                                        <Form.Control
                                            type='text'
                                            placeholder='thefutureinsurance-whatsapp-18586096799'
                                            value={form.botmaker_default_channel_id || ''}
                                            onChange={(e) => handleChange('botmaker_default_channel_id', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className='col-md-6'>
                                    <Form.Group>
                                        <Form.Label className='fw-semibold text-gray-700'>Phone Line Number</Form.Label>
                                        <Form.Control
                                            type='text'
                                            placeholder='18586096799'
                                            value={form.botmaker_phone_line_number || ''}
                                            onChange={(e) => handleChange('botmaker_phone_line_number', e.target.value)}
                                        />
                                        <Form.Text className='text-muted'>Digits only, no + prefix</Form.Text>
                                    </Form.Group>
                                </div>
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>

                    {/* ─── Email SMTP ─── */}
                    <Accordion.Item eventKey='email' className='mt-3'>
                        <Accordion.Header>
                            <div className='d-flex align-items-center gap-2'>
                                <i className='bi bi-envelope fs-4 text-warning' />
                                <span className='fw-bold fs-5'>Email SMTP</span>
                                <Badge bg={form.email_enabled ? 'success' : 'secondary'} className='ms-2'>
                                    {form.email_enabled ? 'Enabled' : 'Disabled'}
                                </Badge>
                            </div>
                        </Accordion.Header>
                        <Accordion.Body>
                            <div className='row g-4'>
                                <div className='col-12'>
                                    <Form.Check
                                        type='switch'
                                        id='user_email_enabled'
                                        label='Enable Email'
                                        checked={form.email_enabled || false}
                                        onChange={(e) => handleChange('email_enabled', e.target.checked)}
                                        className='fs-5'
                                    />
                                </div>
                                <div className='col-md-6'>
                                    <Form.Group>
                                        <Form.Label className='fw-semibold text-gray-700'>SMTP Host</Form.Label>
                                        <Form.Control
                                            type='text'
                                            placeholder='smtp.gmail.com'
                                            value={form.email_host || ''}
                                            onChange={(e) => handleChange('email_host', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className='col-md-3'>
                                    <Form.Group>
                                        <Form.Label className='fw-semibold text-gray-700'>Port</Form.Label>
                                        <Form.Control
                                            type='number'
                                            placeholder='587'
                                            value={form.email_port || 587}
                                            onChange={(e) => handleChange('email_port', parseInt(e.target.value) || 587)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className='col-md-3'>
                                    <Form.Group className='d-flex align-items-end h-100 pb-1'>
                                        <Form.Check
                                            type='switch'
                                            id='user_email_use_tls'
                                            label='Use TLS'
                                            checked={form.email_use_tls !== false}
                                            onChange={(e) => handleChange('email_use_tls', e.target.checked)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className='col-md-6'>
                                    <Form.Group>
                                        <Form.Label className='fw-semibold text-gray-700'>SMTP User</Form.Label>
                                        <Form.Control
                                            type='text'
                                            placeholder='user@example.com'
                                            value={form.email_host_user || ''}
                                            onChange={(e) => handleChange('email_host_user', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className='col-md-6'>
                                    <Form.Group>
                                        <Form.Label className='fw-semibold text-gray-700'>SMTP Password</Form.Label>
                                        <Form.Control
                                            type='text'
                                            autoComplete='off'
                                            placeholder='Enter SMTP password'
                                            value={form.email_host_password || ''}
                                            onChange={(e) => handleChange('email_host_password', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className='col-md-6'>
                                    <Form.Group>
                                        <Form.Label className='fw-semibold text-gray-700'>From Email</Form.Label>
                                        <Form.Control
                                            type='email'
                                            placeholder='notifications@example.com'
                                            value={form.email_from || ''}
                                            onChange={(e) => handleChange('email_from', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className='col-md-6'>
                                    <Form.Group>
                                        <Form.Label className='fw-semibold text-gray-700'>Reply-To Email</Form.Label>
                                        <Form.Control
                                            type='email'
                                            placeholder='support@example.com'
                                            value={form.email_reply_to || ''}
                                            onChange={(e) => handleChange('email_reply_to', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>

                {/* Webhook Info */}
                {config.webhook_token && (
                    <Card className='mt-5 border-dashed border-primary bg-light-primary'>
                        <Card.Body>
                            <div className='d-flex align-items-start gap-3'>
                                <i className='bi bi-link-45deg fs-1 text-primary' />
                                <div>
                                    <h5 className='fw-bold text-gray-900 mb-2'>Webhook URLs</h5>
                                    <p className='text-muted mb-3 fs-7'>
                                        Use these URLs in Botmaker and Twilio to receive incoming messages:
                                    </p>
                                    <div className='bg-white rounded p-3 mb-2'>
                                        <small className='text-muted d-block mb-1'>Botmaker Webhook:</small>
                                        <code className='text-primary fs-7'>
                                            {window.location.origin.replace('3000', '8000')}/webhooks/botmaker/
                                            {config.webhook_token}/
                                        </code>
                                    </div>
                                    <div className='bg-white rounded p-3'>
                                        <small className='text-muted d-block mb-1'>Twilio Webhook:</small>
                                        <code className='text-primary fs-7'>
                                            {window.location.origin.replace('3000', '8000')}/webhooks/twilio/
                                            {config.webhook_token}/
                                        </code>
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                )}
            </div>
        </div>
    )
}
